import { applyCenteredScale } from 'features/knowledgeDocumentViewer/components/viewer/shared/applyCenteredScale'
import { VIEWPORT_REFIT_SCALE_EPSILON } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/shouldRefitToViewportOnResize'
import { useFitToViewport } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useFitToViewport'
import { MAX_SCALE } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useZoomControls'
import { transformWrapperMotionProps } from 'features/knowledgeDocumentViewer/components/viewer/shared/transformWrapperMotionProps'
import { ZoomControls } from 'features/knowledgeDocumentViewer/components/viewer/shared/ZoomControls'
import { GraphStatus } from 'features/knowledgeDocumentViewer/graph/GraphStatus'
import { PreviewGraphOverlay } from 'features/knowledgeDocumentViewer/graph/PreviewGraphOverlay'
import type { GraphLoadState } from 'features/knowledgeDocumentViewer/graph/useDocumentGraph'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'
import styled, { keyframes } from 'styled-components'

import { PreviewCitedTextOverlay } from './PreviewCitedTextOverlay'
import { PreviewPageNavigator } from './PreviewPageNavigator'
import { usePreviewCitedTextFrame } from './usePreviewCitedTextFrame'
import { useSwipePageNavigation } from './useSwipePageNavigation'

const VIEWPORT_FIT_PADDING_PX = 8
const EMPTY_IMAGE_SIZE = { w: 0, h: 0 }

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: var(--muted);
  overflow: hidden;
  position: relative;
`

const Skeleton = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.forecast.cardGroupBackground} 25%,
    ${({ theme }) => theme.forecast.hoverButton} 50%,
    ${({ theme }) => theme.forecast.cardGroupBackground} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`

const ImageWrapper = styled.div<{ $width: number; $height: number }>`
  position: relative;
  display: inline-block;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
`

const StyledImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
  user-select: none;
`

const MeasureImage = styled.img`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`

interface DocumentPreviewViewerProps {
  thumbnailUrl: string | null
  hiResUrl: string | null
  isThumbLoading: boolean
  alt: string
  documentId: string
  currentPage: number
  totalPages: number
  highlightBoxes?: NormalizedBBox[]
  focusBoxes?: NormalizedBBox[]
  graph?: GraphLoadState
  onPageChange: (page: number) => void
  pageNavigationDisabled?: boolean
  showResetZoom?: boolean
  onToggleFullscreen?: () => void
  isFullscreen?: boolean
}

export const DocumentPreviewViewer: React.FC<DocumentPreviewViewerProps> = ({
  thumbnailUrl,
  hiResUrl,
  isThumbLoading,
  alt,
  documentId,
  currentPage,
  totalPages,
  highlightBoxes,
  focusBoxes,
  graph,
  onPageChange,
  pageNavigationDisabled = false,
  showResetZoom = true,
  onToggleFullscreen,
  isFullscreen = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null)
  const [minScale, setMinScale] = useState(0.01)
  const [currentScale, setCurrentScale] = useState(1)
  const [imgNatural, setImgNatural] = useState<{
    w: number
    h: number
  } | null>(null)
  const [hiResReady, setHiResReady] = useState(false)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)

  const { resetToFit, onFitComplete } = useFitToViewport(
    transformRef,
    setMinScale,
    imgNatural ?? EMPTY_IMAGE_SIZE,
    false,
    imgNatural !== null,
    VIEWPORT_FIT_PADDING_PX,
    true,
    null,
    `${documentId}:${currentPage}`
  )

  const applyNaturalSize = useCallback((img: HTMLImageElement) => {
    if (img.naturalWidth === 0 || img.naturalHeight === 0) return
    setImgNatural({ w: img.naturalWidth, h: img.naturalHeight })
    setInitialLoadComplete(true)
  }, [])

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      applyNaturalSize(e.currentTarget)
    },
    [applyNaturalSize]
  )

  const handleMeasureImageRef = useCallback(
    (el: HTMLImageElement | null) => {
      if (el?.complete) applyNaturalSize(el)
    },
    [applyNaturalSize]
  )

  useEffect(() => {
    setImgNatural(null)
    setMinScale(0.01)
    setCurrentScale(1)
    setHiResReady(false)
    setInitialLoadComplete(false)
  }, [documentId])

  useEffect(() => {
    setImgNatural(null)
    setMinScale(0.01)
    setCurrentScale(1)
    setHiResReady(false)
  }, [currentPage])

  usePreviewCitedTextFrame({
    transformRef,
    boxes: focusBoxes ?? highlightBoxes,
    isFitted: imgNatural !== null && onFitComplete,
    minScale,
    resetToFit,
  })

  useEffect(() => {
    if (!hiResUrl) return
    setHiResReady(false)
    const img = new Image()
    img.src = hiResUrl
    img.onload = () => setHiResReady(true)
    return () => {
      img.onload = null
    }
  }, [hiResUrl])

  const zoomIn = useCallback(() => {
    applyCenteredScale(transformRef.current, (scale) => Math.min(scale * 2, 10))
  }, [])

  const zoomOut = useCallback(() => {
    applyCenteredScale(transformRef.current, (scale) =>
      Math.max(scale / 2, minScale)
    )
  }, [minScale])

  const handleTransformed = useCallback((pinchRef: ReactZoomPanPinchRef) => {
    setCurrentScale(pinchRef.instance.transformState.scale)
  }, [])

  useEffect(() => {
    const scale = transformRef.current?.instance.transformState.scale
    if (scale === undefined) return
    setCurrentScale(scale)
  }, [minScale, onFitComplete])

  const isPageAtFitScale = useCallback(() => {
    const scale = transformRef.current?.instance.transformState.scale
    if (scale === undefined) return true
    return scale - minScale < VIEWPORT_REFIT_SCALE_EPSILON
  }, [minScale])

  useSwipePageNavigation({
    containerRef,
    enabled: totalPages > 1 && !pageNavigationDisabled,
    currentPage,
    totalPages,
    onPageChange,
    isSwipeAllowed: isPageAtFitScale,
  })

  const showSkeleton = !initialLoadComplete && isThumbLoading
  const showViewer = !showSkeleton && !!thumbnailUrl
  const zoomOutDisabled =
    Math.abs(currentScale - minScale) < VIEWPORT_REFIT_SCALE_EPSILON
  const zoomInDisabled =
    Math.abs(currentScale - MAX_SCALE) < VIEWPORT_REFIT_SCALE_EPSILON ||
    currentScale >= MAX_SCALE

  return (
    <Container ref={containerRef}>
      {showSkeleton && <Skeleton />}
      {showViewer && (
        <TransformWrapper
          ref={transformRef}
          minScale={minScale}
          maxScale={10}
          limitToBounds
          centerOnInit={false}
          wheel={{ step: 0.5 }}
          doubleClick={{ disabled: false, mode: 'zoomIn' }}
          pinch={{ disabled: false }}
          {...transformWrapperMotionProps()}
          onTransformed={handleTransformed}>
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
              cursor: 'grab',
            }}
            contentStyle={{
              display: 'inline-block',
              opacity: onFitComplete ? 1 : 0,
            }}>
            {imgNatural ? (
              <ImageWrapper $width={imgNatural.w} $height={imgNatural.h}>
                <StyledImage
                  src={hiResReady && hiResUrl ? hiResUrl : thumbnailUrl}
                  alt={alt}
                  draggable={false}
                />
                {graph?.pageGraphs.length ? (
                  <PreviewGraphOverlay
                    pageGraphs={graph.pageGraphs}
                    imageNatural={imgNatural}
                    pxPerImagePx={currentScale}
                  />
                ) : null}
                {highlightBoxes?.length ? (
                  <PreviewCitedTextOverlay
                    boxes={highlightBoxes}
                    imageNatural={imgNatural}
                  />
                ) : null}
              </ImageWrapper>
            ) : null}
            {!imgNatural && (
              <MeasureImage
                ref={handleMeasureImageRef}
                src={thumbnailUrl}
                alt=''
                onLoad={handleImageLoad}
              />
            )}
          </TransformComponent>
        </TransformWrapper>
      )}
      {showViewer ? (
        <ZoomControls
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onResetZoom={resetToFit}
          showResetZoom={showResetZoom}
          onToggleFullscreen={onToggleFullscreen}
          isFullscreen={isFullscreen}
          disabled={!imgNatural || !onFitComplete}
          zoomOutDisabled={zoomOutDisabled}
          zoomInDisabled={zoomInDisabled}
        />
      ) : null}
      {graph?.message ? (
        <GraphStatus
          message={graph.message}
          failed={graph.failed}
          retry={graph.retry}
          placement='center'
        />
      ) : null}
      <PreviewPageNavigator
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
        disabled={pageNavigationDisabled}
      />
    </Container>
  )
}
